/**
 * ImportComposition - Composição Visual do Preço de Importação (Cascata)
 * Mostra de onde vem o Preço de Importação IEA/CNA
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Ship, ArrowDown } from 'lucide-react';

const ImportComposition = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/market/import-price-composition`);
      if (res.ok) {
        const json = await res.json();
        setData(json);
      }
    } catch (err) {
      console.error('Erro ao carregar composição:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !data) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto" />
        </CardContent>
      </Card>
    );
  }

  const getStepStyle = (etapa) => {
    if (etapa.startsWith('=') && etapa.includes('Preço de Importação')) return 'bg-blue-100 border-blue-400 text-blue-900 font-bold';
    if (etapa.startsWith('=') && etapa.includes('CIF')) return 'bg-gray-100 border-gray-400 text-gray-800 font-bold';
    if (etapa.startsWith('=') && etapa.includes('Valor em')) return 'bg-green-50 border-green-300 text-green-800';
    if (etapa.startsWith('+')) return 'bg-amber-50 border-amber-200 text-amber-900';
    if (etapa.startsWith('×')) return 'bg-purple-50 border-purple-200 text-purple-800';
    return 'bg-white border-gray-200 text-gray-800';
  };

  return (
    <Card data-testid="import-composition">
      <CardHeader className="pb-2">
        <CardTitle className="text-gray-800 flex items-center gap-2">
          <Ship className="w-5 h-5 text-blue-600" />
          {data.title}
        </CardTitle>
        <p className="text-sm text-gray-500">
          Entenda cada parcela que forma o preço de R$ {data.preco_importacao_oficial?.toFixed(2)}/kg
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-1">
          {data.composicao?.map((step, idx) => (
            <div key={idx}>
              <div className={`p-3 rounded-lg border ${getStepStyle(step.etapa)} flex items-center justify-between`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{step.etapa}</div>
                  <div className="text-xs opacity-75">{step.descricao}</div>
                </div>
                <div className="text-right flex-shrink-0 ml-3">
                  {step.valor_brl && (
                    <div className="font-mono font-bold">R$ {step.valor_brl.toFixed(2)}</div>
                  )}
                  {step.percentual && (
                    <div className="text-xs opacity-60">{step.percentual}%</div>
                  )}
                </div>
              </div>
              {idx < data.composicao.length - 1 && !data.composicao[idx + 1].etapa.startsWith('=') && (
                <div className="flex justify-center py-0.5">
                  <ArrowDown className="w-3 h-3 text-gray-300" />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Comparação oficial vs calculado */}
        {data.oficial_vs_calculado && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              <strong>Valor oficial CNA/IEA:</strong> R$ {data.oficial_vs_calculado.oficial?.toFixed(2)}/kg |
              <strong> Estimativa:</strong> R$ {data.oficial_vs_calculado.calculado?.toFixed(2)}/kg
            </p>
            <p className="text-xs text-blue-600 mt-1">{data.oficial_vs_calculado.nota}</p>
          </div>
        )}

        <p className="text-xs text-gray-400 mt-3 text-center">{data.nota_importante}</p>
      </CardContent>
    </Card>
  );
};

export default ImportComposition;
